import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Video, Users, Shield, Zap, Globe, Clock, ArrowRight, Mic, MicOff } from 'lucide-react';
import { authService } from '../../services/auth.service.ts';
import { FREE_MEETING_MAX_DURATION_MINUTES } from '../../utils/plan.ts';

/** Hero mockup — yerel Unsplash görselleri (public/images/hero/, LICENSE.txt) */
const HERO_HOST = {
  name: 'Elif Şahin',
  role: 'Toplantı sahibi',
  image: '/images/hero/host.jpg',
};

const HERO_PARTICIPANTS = [
  {
    name: 'Ayşe K.',
    image: '/images/hero/p1.jpg',
    kenClass: 'hero-ken-burns-1',
    objectPosition: 'center 22%',
    speaking: true,
    micOn: true,
    enterDelay: '0ms',
  },
  {
    name: 'Mehmet Y.',
    image: '/images/hero/p2.jpg',
    kenClass: 'hero-ken-burns-2',
    objectPosition: 'center 25%',
    micOn: true,
    enterDelay: '80ms',
  },
  {
    name: 'Zeynep A.',
    image: '/images/hero/p3.jpg',
    kenClass: 'hero-ken-burns-3',
    objectPosition: 'center 20%',
    micOn: false,
    enterDelay: '160ms',
  },
  {
    name: 'Can D.',
    image: '/images/hero/p4.jpg',
    kenClass: 'hero-ken-burns-4',
    objectPosition: 'center 24%',
    micOn: true,
    enterDelay: '240ms',
  },
] as const;

const Home: React.FC = () => {
  const [user, setUser] = useState(() => authService.getUser());

  useEffect(() => {
    const syncUser = () => setUser(authService.getUser());
    window.addEventListener('storage', syncUser);
    return () => window.removeEventListener('storage', syncUser);
  }, []);

  const isLoggedIn = !!user;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-purple-500/30 rounded-full blur-3xl -top-48 -left-48 animate-pulse"></div>
          <div className="absolute w-96 h-96 bg-blue-500/30 rounded-full blur-3xl -bottom-48 -right-48 animate-pulse"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="space-y-6 md:space-y-8">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
                Toplantıları
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                  Yeniden Tanımlayın
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-300">
                Kristal netliğinde görüntü, gecikme yok. TBKMeet ile takımınızı bir araya getirin ve işbirliğini zirveye taşıyın.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                {isLoggedIn ? (
                  <Link
                    to="/pricing"
                    className="group px-6 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold text-base sm:text-lg transition transform hover:scale-105 hover:shadow-2xl flex items-center justify-center"
                  >
                    Size uygun planları görün
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition" />
                  </Link>
                ) : (
                  <Link
                    to="/register"
                    className="group px-6 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold text-base sm:text-lg transition transform hover:scale-105 hover:shadow-2xl flex items-center justify-center"
                  >
                    Ücretsiz Dene
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition" />
                  </Link>
                )}
                <button className="px-6 sm:px-8 py-3.5 sm:py-4 bg-white/10 backdrop-blur-lg text-white rounded-lg font-semibold text-base sm:text-lg border border-white/20 hover:bg-white/20 transition">
                  Demo İzle
                </button>
              </div>
            </div>
            
            <div className="relative hero-animate-float">
              <div className="relative bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl p-4 sm:p-8 backdrop-blur-sm border border-white/10 shadow-2xl">
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl blur hero-animate-shimmer" />
                <div className="relative bg-slate-900/90 rounded-xl p-4 sm:p-5 space-y-4 overflow-hidden ring-1 ring-white/10">
                  {/* Üst bar — host */}
                  <div className="flex items-center justify-between gap-3 pb-3 border-b border-white/10">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="relative flex-shrink-0">
                        <img
                          src={HERO_HOST.image}
                          alt=""
                          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover ring-2 ring-purple-500/60"
                          loading="eager"
                        />
                        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-slate-900" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">{HERO_HOST.name}</p>
                        <p className="text-gray-400 text-xs truncate">{HERO_HOST.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 px-2 py-1 rounded-full bg-red-500/15 border border-red-500/30">
                      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                      <span className="text-red-400 text-[10px] sm:text-xs font-semibold tracking-wide">CANLI</span>
                    </div>
                  </div>

                  {/* 2x2 video grid */}
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    {HERO_PARTICIPANTS.map((p) => (
                      <div
                        key={p.name}
                        className={`hero-tile-enter relative aspect-video rounded-lg overflow-hidden bg-slate-800 ${
                          p.speaking ? 'ring-2 ring-green-500/80 ring-offset-1 ring-offset-slate-900 hero-speaking-ring' : ''
                        }`}
                        style={{ animationDelay: p.enterDelay }}
                      >
                        <div className={`absolute inset-0 ${p.kenClass}`}>
                          <img
                            src={p.image}
                            alt={p.name}
                            className="w-full h-full object-cover"
                            style={{ objectPosition: p.objectPosition }}
                            loading="lazy"
                          />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent pointer-events-none" />
                        {p.speaking && (
                          <div className="absolute top-1.5 left-1.5 flex items-center gap-1.5 bg-green-600/95 px-2 py-0.5 rounded-md shadow-lg">
                            <div className="flex items-end gap-0.5 h-3" aria-hidden>
                              <span className="w-0.5 bg-white rounded-full hero-audio-bar" style={{ animationDelay: '0s' }} />
                              <span className="w-0.5 bg-white rounded-full hero-audio-bar" style={{ animationDelay: '0.15s' }} />
                              <span className="w-0.5 bg-white rounded-full hero-audio-bar" style={{ animationDelay: '0.3s' }} />
                            </div>
                            <span className="text-[9px] sm:text-[10px] text-white font-semibold">Konuşuyor</span>
                          </div>
                        )}
                        <div className="absolute bottom-1.5 left-1.5 right-1.5 flex items-center justify-between gap-1">
                          <span className="text-white text-[10px] sm:text-xs font-medium truncate drop-shadow-md">
                            {p.name}
                          </span>
                          <span
                            className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center backdrop-blur-sm ${
                              p.micOn ? 'bg-black/45' : 'bg-red-600/95'
                            }`}
                            aria-hidden
                          >
                            {p.micOn ? (
                              <Mic className="w-2.5 h-2.5 text-white" />
                            ) : (
                              <MicOff className="w-2.5 h-2.5 text-white" />
                            )}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="text-center text-[10px] text-gray-600 pt-1">
                    4 katılımcı • HD görüntü
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Güçlü Özellikler
            </h2>
            <p className="text-xl text-gray-400">
              Modern toplantılar için ihtiyacınız olan her şey
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Video, title: "HD Görüntü Kalitesi", desc: "4K'ya kadar kristal net görüntü kalitesi" },
              { icon: Shield, title: "AES-256 Şifreleme", desc: "Sohbet AES-256-GCM; medya DTLS-SRTP ile korunur" },
              { icon: Zap, title: "Anında Bağlantı", desc: "Bir tıkla toplantıya katılın" },
              { icon: Users, title: "Sınırsız Katılımcı", desc: "İstediğiniz kadar kişiyi davet edin" },
              { icon: Globe, title: "Global Erişim", desc: "Dünyanın her yerinden bağlanın" },
              { icon: Clock, title: "7/24 Destek", desc: "Her zaman yanınızdayız" }
            ].map((feature, i) => (
              <div key={i} className="group p-8 bg-white/5 backdrop-blur-lg rounded-2xl border border-white/10 hover:bg-white/10 hover:border-purple-500/50 transition transform hover:-translate-y-2">
                <feature.icon className="w-12 h-12 text-purple-400 mb-4 group-hover:scale-110 transition" />
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product truths */}
      <section className="py-20 px-6 bg-gradient-to-r from-purple-900/50 to-pink-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              { value: `${FREE_MEETING_MAX_DURATION_MINUTES} dk`, label: 'Ücretsiz toplantı süresi' },
              { value: 'AES-256', label: 'Sohbet şifreleme' },
              { value: '1 tık', label: 'Anında toplantı başlat' },
              { value: '%100', label: 'Türkçe arayüz' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-5xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Bugün Başlayın
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            {isLoggedIn
              ? 'İhtiyacınıza uygun planı seçin ve toplantılarınızı bir üst seviyeye taşıyın.'
              : 'Kredi kartı gerekmez. 14 gün ücretsiz deneme.'}
          </p>
          {isLoggedIn ? (
            <Link
              to="/pricing"
              className="inline-block px-12 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold text-lg transition transform hover:scale-105 hover:shadow-2xl"
            >
              Size uygun planları görün
            </Link>
          ) : (
            <Link
              to="/register"
              className="inline-block px-12 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold text-lg transition transform hover:scale-105 hover:shadow-2xl"
            >
              Ücretsiz Hesap Oluştur
            </Link>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;